import React, { useEffect, useId, useRef } from 'react';
import { Camera, Image as ImageIcon, FileUp, X } from 'lucide-react';

// Tile metadata, keyed by the source id that `onSelect` reports back.
const SOURCE_META = {
    camera: { icon: Camera, label: 'Camera' },
    photos: { icon: ImageIcon, label: 'Photos' },
    files: { icon: FileUp, label: 'Files' },
};

/**
 * Bottom-anchored action sheet for choosing where an upload comes from.
 *
 * This is a short chooser, not a data-entry form — the same class of surface as
 * ConfirmationModal, whose escape/backdrop/aria idioms it copies
 * (V2Components.tsx). It deliberately isn't a ConfirmationModal variant:
 * that component takes no children and hardcodes a two-button footer.
 *
 * Rendered by useFilePicker on Android only; see utils/platform.js for why.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {(source: 'camera' | 'photos' | 'files') => void} props.onSelect
 * @param {Array<'camera' | 'photos' | 'files'>} props.sources - Ordered tiles to
 *   render. Image-only pickers omit 'files' (a file browse adds nothing when the
 *   accept string is already `image/*`).
 * @param {string} [props.title='Add a file']
 */
const FileSourceSheet = ({ isOpen, onClose, onSelect, sources = [], title = 'Add a file' }) => {
    const titleId = useId();
    const panelRef = useRef(null);
    const previouslyFocusedRef = useRef(null);

    // onClose is typically an inline arrow from the caller, so its identity
    // changes every render. Reading it through a ref keeps the focus effect
    // below keyed on `isOpen` alone — depending on onClose directly would
    // re-run the effect mid-interaction, and its cleanup would yank focus back
    // to the trigger while the sheet is still open.
    const onCloseRef = useRef(onClose);
    useEffect(() => {
        onCloseRef.current = onClose;
    });

    // Focus management for the modal dialog: move focus in on open, keep Tab
    // inside while open, and hand focus back to the trigger on close.
    useEffect(() => {
        if (!isOpen) return undefined;

        previouslyFocusedRef.current = document.activeElement;

        const focusableInPanel = () =>
            Array.from(panelRef.current?.querySelectorAll('button:not([disabled])') || []);

        // First tile rather than the Close button: the tiles are why the sheet
        // opened, and Shift+Tab reaches Close immediately anyway.
        const tiles = focusableInPanel();
        (tiles.find(el => el.dataset.sourceTile) || tiles[0])?.focus();

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onCloseRef.current?.();
                return;
            }
            if (e.key !== 'Tab') return;

            const focusable = focusableInPanel();
            if (focusable.length === 0) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement;
            const outside = !panelRef.current?.contains(active);

            if (e.shiftKey && (active === first || outside)) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && (active === last || outside)) {
                e.preventDefault();
                first.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            const trigger = previouslyFocusedRef.current;
            // Guard: the trigger can be unmounted by the time we close (e.g. a
            // slot that filled up and swapped its button out).
            if (trigger && typeof trigger.focus === 'function' && document.contains(trigger)) {
                trigger.focus();
            }
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div
            // z-[70] clears both ConfirmationModal (z-[60]) and the ad-hoc
            // z-50 modals this sheet can be opened from (e.g. PaintFormModal).
            className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            // Stable hook for E2E: the title is caller-supplied and varies per
            // slot ("Add Receipt", "Add Manufacturer's Tag", ...), so page
            // objects must not locate the sheet by its accessible name.
            data-testid="file-source-sheet"
        >
            <div
                ref={panelRef}
                className="bg-[var(--bg-card)] w-full max-w-lg rounded-t-2xl shadow-xl px-4 pt-3 pb-[calc(1.5rem+env(safe-area-inset-bottom))] theme-transition"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Grab handle — affordance only, the sheet isn't drag-dismissible */}
                <div className="w-10 h-1 rounded-full bg-[var(--border-visible)] mx-auto mb-3" aria-hidden="true" />

                <div className="relative flex items-center justify-center mb-5">
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="absolute left-0 p-1 rounded-full text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] transition"
                    >
                        <X size={20} />
                    </button>
                    <h3 id={titleId} className="text-base font-bold text-[var(--text-main)] theme-transition">
                        {title}
                    </h3>
                </div>

                <div className="flex gap-3">
                    {sources.map((source) => {
                        const meta = SOURCE_META[source];
                        if (!meta) return null;
                        const Icon = meta.icon;
                        return (
                            <button
                                key={source}
                                type="button"
                                data-source-tile={source}
                                onClick={() => onSelect(source)}
                                className="flex-1 flex flex-col items-center justify-center gap-2 py-5 rounded-xl border border-[var(--border-visible)] text-[var(--text-main)] hover:bg-[var(--bg-hover)] active:bg-[var(--bg-hover)] transition theme-transition"
                            >
                                <Icon size={24} className="text-[var(--text-secondary)]" />
                                <span className="text-sm font-medium">{meta.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default FileSourceSheet;
